import { useEffect, useMemo } from 'react';
import type { AdpAnalytics } from '@adsk/adp-web-analytics-sdk';
import { useSiteConfig } from '@innovyze/stylovyze';
import {
  initBernoulliAdp,
  type RuntimeAdpConfig,
} from '@/services/analytics/adp';
import { BERNOULLI_ADP_KEY } from '@/config/adp.site-config';

export const BERNOULLI_ASSISTANT_ADP_ENV_STORAGE_KEY =
  'bernoulli_assistant_adp_env';

export const readAssistantAdpEnvFromLocalStorage = (): string | undefined => {
  if (typeof window === 'undefined') {
    return undefined;
  }
  try {
    const raw = window.localStorage.getItem(
      BERNOULLI_ASSISTANT_ADP_ENV_STORAGE_KEY,
    );
    if (raw === null) {
      return undefined;
    }
    const trimmed = raw.trim();
    return trimmed === '' ? undefined : trimmed;
  } catch {
    return undefined;
  }
};

export const buildAssistantEmbedRuntimeAdpConfig = (
  adpConfig: RuntimeAdpConfig | undefined,
): RuntimeAdpConfig | undefined => {
  const overrideEnv = readAssistantAdpEnvFromLocalStorage();
  const baseEnv: NonNullable<RuntimeAdpConfig['env']> = adpConfig?.env ?? 'dev';
  const env = (overrideEnv ?? baseEnv) as NonNullable<RuntimeAdpConfig['env']>;
  if (!adpConfig) {
    return { env };
  }
  return { ...adpConfig, env };
};

type WebContainerLike = {
  getSessionId?: () => string;
};

const getHostSessionId = () => {
  const hostBridge = (window as Window & { WebContainer?: WebContainerLike })
    .WebContainer;
  return typeof hostBridge?.getSessionId === 'function'
    ? hostBridge.getSessionId()
    : undefined;
};

export const useAssistantAdpClient = (): AdpAnalytics | null => {
  const { config } = useSiteConfig();

  const adpClient = useMemo(() => {
    const adpConfig = config?.adp;
    const hostReferrer =
      adpConfig?.id && adpConfig.name && adpConfig.key !== BERNOULLI_ADP_KEY
        ? {
            id: adpConfig.id,
            name: adpConfig.name,
            sessionId: getHostSessionId(),
          }
        : undefined;
    return initBernoulliAdp({
      runtimeConfig: buildAssistantEmbedRuntimeAdpConfig(adpConfig),
      hostReferrer,
    });
  }, [config]);

  useEffect(() => {
    adpClient?.enableHostProductData?.();
  }, [adpClient]);

  return adpClient ?? null;
};
